import { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Tag,
  Space,
  Row,
  Col,
  Tooltip,
  message,
  Card,
  Typography,
  Radio,
  Badge,
  Spin,
  Table,
  Dropdown,
  Select,
  Input
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  TableOutlined,
  AppstoreOutlined,
  MoreOutlined
} from '@ant-design/icons';
import type { Task, TaskStatus, TaskPriority, TaskType } from '@/types/employee/task';
import { getTasks, deleteTask, updateTask } from '@/api/tasks';
import { useAuth } from '@/contexts/AuthContext';
import TaskModal from '@/components/features/employee/TaskModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommonCalendar from '@/components/common/Calendar';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Button: RadioButton } = Radio;
const { Option } = Select;

const TASK_STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'pending', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const TASK_PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const TASK_TYPES: { value: TaskType; label: string }[] = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' }
];

type ViewMode = 'board' | 'calendar' | 'table';

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export default function Tasks() {
  const { t } = useTranslation(['tasks', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // State
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // State for pagination and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority | null>(null);
  const [selectedType, setSelectedType] = useState<TaskType | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Queries
  const { data: tasksData = { tasks: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['tasks', pagination.current, pagination.pageSize, searchTerm, selectedStatus, selectedPriority, selectedType, firestoreUser?.workspace?.id],
    queryFn: () => getTasks({ 
      firestoreUser: firestoreUser!,
      searchTerm,
      status: selectedStatus || undefined,
      priority: selectedPriority || undefined,
      type: selectedType || undefined,
      pageSize: pagination.pageSize,
      page: pagination.current
    }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Mutations
  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => deleteTask(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('tasks:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setIsDeleteModalVisible(false);
      setSelectedTask(null);
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Task> }) => 
      updateTask(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('tasks:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  // Memoized values
  const taskList = useMemo(() => tasksData?.tasks || [], [tasksData]);
  
  const tasksByStatus = useMemo(() => ({
    pending: taskList.filter(task => task.status === 'pending'),
    in_progress: taskList.filter(task => task.status === 'in_progress'),
    completed: taskList.filter(task => task.status === 'completed'),
    cancelled: taskList.filter(task => task.status === 'cancelled')
  }), [taskList]);

  // Utility functions
  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'processing';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'blue';
      default:
        return 'green';
    }
  };

  const getTypeColor = (type: TaskType) => {
    switch (type) {
      case 'maintenance':
        return 'blue';
      case 'cleaning':
        return 'green';
      case 'inspection':
        return 'orange';
      case 'security':
        return 'purple';
      default:
        return 'default';
    }
  };

  const handleAddTask = useCallback(() => {
    setSelectedTask(null);
    setIsModalOpen(true);
  }, []);

  const handleEditTask = useCallback((task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  }, []);

  const handleViewTask = useCallback((task: Task) => {
    navigate(`/employee/tasks/${task.id}`, { 
      state: { 
        task,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteTask = useCallback((task: Task) => {
    setSelectedTask(task);
    setIsDeleteModalVisible(true);
  }, []);

  const handleStatusChange = useCallback((taskId: string, newStatus: TaskStatus) => {
    updateTaskMutation.mutate({ id: taskId, data: { status: newStatus } });
  }, [updateTaskMutation]);

  const handlePriorityChange = useCallback((taskId: string, newPriority: TaskPriority) => {
    updateTaskMutation.mutate({ id: taskId, data: { priority: newPriority } });
  }, [updateTaskMutation]);

  const handleEmployeeClick = useCallback((employeeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/resources/employees/${employeeId}`);
  }, [navigate]);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTask(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTask(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedTask) {
      deleteTaskMutation.mutate(selectedTask.id);
    }
  }, [selectedTask, deleteTaskMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedTask(null);
  }, []);

  const renderTaskCard = useCallback((task: Task) => {
    const dropdownItems = [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: t('common:actions.edit'),
        onClick: (e: any) => {
          e.stopPropagation();
          handleEditTask(task);
        }
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('common:actions.delete'),
        danger: true,
        onClick: (e: any) => {
          e.stopPropagation();
          handleDeleteTask(task);
        }
      }
    ];

    return (
      <Card
        key={task.id}
        size="small"
        style={{ 
          margin: '8px', 
          minHeight: '160px',
          cursor: 'pointer',
          position: 'relative'
        }}
        onClick={() => handleViewTask(task)}
        hoverable
      >
        {/* Dropdown menu in top right */}
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Dropdown
            menu={{ items: dropdownItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>

        <div>
          <div style={{ marginBottom: '8px', paddingRight: '32px' }}>
            <Text strong style={{ fontSize: '14px' }}>{task.title}</Text>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Space size="small">
              <Tag color={getTypeColor(task.type)}>
                {TASK_TYPES.find(t => t.value === task.type)?.label}
              </Tag>
            </Space>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Assigned to:{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                onClick={(e) => handleEmployeeClick(task.assignedTo.id, e)}
              >
                {task.assignedTo.name}
              </Button>
            </Text>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Due: {dayjs(task.dueDate).format('MMM D, YYYY')}
            </Text>
          </div>
          
          {/* Editable Status and Priority */}
          <Row gutter={8} style={{ marginTop: '8px' }}>
            <Col span={12}>
              <Select
                size="small"
                value={task.status}
                onChange={(value) => handleStatusChange(task.id, value)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%' }}
                dropdownStyle={{ fontSize: '12px' }}
              >
                {TASK_STATUSES.map(status => (
                  <Option key={status.value} value={status.value} style={{ fontSize: '12px' }}>
                    <Tag color={getStatusColor(status.value)} style={{ fontSize: '9px' }}>
                      {status.label}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <Select
                size="small"
                value={task.priority}
                onChange={(value) => handlePriorityChange(task.id, value)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%' }}
                dropdownStyle={{ fontSize: '12px' }}
              >
                {TASK_PRIORITIES.map(priority => (
                  <Option key={priority.value} value={priority.value} style={{ fontSize: '12px' }}>
                    <Tag color={getPriorityColor(priority.value)} style={{ fontSize: '11px' }}>
                      {priority.label}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      </Card>
    );
  }, [handleViewTask, handleEditTask, handleDeleteTask, handleStatusChange, handlePriorityChange, handleEmployeeClick, t]);

  const renderBoardView = useCallback(() => (
    <div>
      <Row gutter={[16, 16]}>
        {TASK_STATUSES.map(status => (
          <Col xs={24} sm={12} md={6} key={status.value}>
            <Card
              title={
                <Space>
                  <Tag color={getStatusColor(status.value)}>
                    {status.label}
                  </Tag>
                  <Badge count={tasksByStatus[status.value].length} />
                </Space>
              }
              size="small"
            >
              {tasksByStatus[status.value].length > 0 ? (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {tasksByStatus[status.value].map(task => renderTaskCard(task))}
                </div>
              ) : (
                <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                  No {status.label.toLowerCase()} tasks
                </Text>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  ), [tasksByStatus, renderTaskCard]);

  const renderCalendarView = useCallback(() => (
    <CommonCalendar
      events={taskList}
      selectedDate={selectedDate}
      onSelectDate={setSelectedDate}
      eventTypeColors={{
        maintenance: 'blue',
        cleaning: 'green',
        inspection: 'orange',
        security: 'purple',
        other: 'default'
      }}
      getEventDate={(task: Task) => dayjs(task.dueDate)}
      getEventTitle={(task: Task) => task.title}
      getEventType={(task: Task) => task.type}
      getEventColor={(task: Task) => {
        switch (task.type) {
          case 'maintenance': return 'blue';
          case 'cleaning': return 'green';
          case 'inspection': return 'orange';
          case 'security': return 'purple';
          default: return 'default';
        }
      }}
      onEventClick={(task: Task) => handleViewTask(task)}
      showEventCount={false}
      defaultView="month"
    />
  ), [taskList, selectedDate, handleViewTask]);

  const renderTableView = useCallback(() => {
    const columns = [
      {
        title: t('tasks:title'),
        dataIndex: 'title',
        key: 'title',
        render: (text: string, record: Task) => (
          <Button type="link" onClick={() => handleViewTask(record)}>
            {text}
          </Button>
        ),
      },
      {
        title: t('tasks:typeLabel'),
        dataIndex: 'type',
        key: 'type',
        render: (type: TaskType) => (
          <Tag color={getTypeColor(type)}>
            {TASK_TYPES.find(t => t.value === type)?.label}
          </Tag>
        ),
      },
      {
        title: t('tasks:statusLabel'),
        dataIndex: 'status',
        key: 'status',
        render: (status: TaskStatus) => (
          <Tag color={getStatusColor(status)}>
            {TASK_STATUSES.find(s => s.value === status)?.label}
          </Tag>
        ),
      },
      {
        title: t('tasks:priorityLabel'),
        dataIndex: 'priority',
        key: 'priority',
        render: (priority: TaskPriority) => (
          <Tag color={getPriorityColor(priority)}>
            {TASK_PRIORITIES.find(p => p.value === priority)?.label}
          </Tag>
        ),
      },
      {
        title: t('tasks:assignedTo'),
        dataIndex: ['assignedTo', 'name'],
        key: 'assignedTo',
        render: (name: string, record: Task) => (
          <Button
            type="link"
            size="small"
            onClick={(e) => handleEmployeeClick(record.assignedTo.id, e)}
          >
            {name}
          </Button>
        ),
      },
      {
        title: t('tasks:dueDate'),
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (date: Date) => dayjs(date).format('MMM D, YYYY'),
        sorter: (a: Task, b: Task) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      },
      {
        title: t('common:actions.title'),
        key: 'actions',
        render: (record: Task) => (
          <Space>
            <Tooltip title={t('common:actions.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditTask(record)}
              />
            </Tooltip>
            <Tooltip title={t('common:actions.delete')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteTask(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Table
        dataSource={taskList}
        columns={columns}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tasksData?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ 
              ...prev, 
              current: page, 
              pageSize: pageSize || prev.pageSize 
            }));
          }
        }}
        scroll={{ x: 1200 }}
      />
    );
  }, [taskList, handleViewTask, handleEditTask, handleDeleteTask, handleEmployeeClick, t]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderView = () => {
    switch (viewMode) {
      case 'board':
        return renderBoardView();
      case 'calendar':
        return renderCalendarView();
      case 'table':
        return renderTableView();
      default:
        return renderBoardView();
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>{t('tasks:title')}</Title>
        </Col>
        <Col>
          <Space>
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
            >
              <RadioButton value="board">
                <AppstoreOutlined /> {t('tasks:boardView')}
              </RadioButton>
              <RadioButton value="calendar">
                <CalendarOutlined /> {t('tasks:calendarView')}
              </RadioButton>
              <RadioButton value="table">
                <TableOutlined /> {t('tasks:tableView')}
              </RadioButton>
            </Radio.Group>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTask}
            >
              {t('tasks:addTask')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Input.Search
            placeholder={t('common:search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            onSearch={() => setPagination(prev => ({ ...prev, current: 1 }))}
          />
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder={t('tasks:statusLabel')}
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            allowClear
            style={{ width: '100%' }}
          >
            {TASK_STATUSES.map(status => (
              <Option key={status.value} value={status.value}>
                {status.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder={t('tasks:priorityLabel')}
            value={selectedPriority}
            onChange={(value) => {
              setSelectedPriority(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            allowClear
            style={{ width: '100%' }}
          >
            {TASK_PRIORITIES.map(priority => (
              <Option key={priority.value} value={priority.value}>
                {priority.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder={t('tasks:typeLabel')}
            value={selectedType}
            onChange={(value) => {
              setSelectedType(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            allowClear
            style={{ width: '100%' }}
          >
            {TASK_TYPES.map(type => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {renderView()}

      {/* Task Modal */}
      <TaskModal
        open={isModalOpen}
        task={selectedTask}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('tasks:deleteConfirm')}
      />
    </div>
  );
} 